/**
 * createCollectionStore
 *
 * Singleton Valtio store for a Firestore collection.
 * Returns the same instance for the same path.
 *
 * **Lifecycle:** Call `connect()` in major components (ArTable, Form).
 * Never call in leaf components (ArCell, FieldUI) — they receive
 * the store from their parent and read via `useSnapshot(store.state)`.
 *
 * **Read:** `useSnapshot(store.state)` for reactive reads.
 * **Optimistic write:** mutate `store.state.docs.get(id).data` directly.
 * **Persist:** `store.update()` / `store.set()` / `store.add()` / `store.delete()`.
 *
 * @example
 * ```ts
 * const rows = createCollectionStore<ArRowData>('forms/f1/v1/products');
 * rows.connect({ queryOptions: { orderBy: { field: 'order', direction: 'asc' } } });
 *
 * // Read
 * const snap = useSnapshot(rows.state);
 *
 * // Write (consumer updates both)
 * rows.state.docs.get(rowId).data.cells[colId].value = 42;
 * rows.update(rowId, { [`cells.${colId}.value`]: 42 });
 * ```
 */

import type { UpdateData, WithFieldValue } from 'firebase/firestore';
import type { CollectionState, CollectionPersist, PersistOperationOptions, QueryOptions } from '../Store.types';
import { createCollectionState } from '../state/createCollectionState';
import { persistCollection } from '../persist/persistCollection';
import { registry } from './registry';

export type CollectionStoreOptions = {
  queryOptions?: QueryOptions;
  /** Bypass the registry; return a fresh per-instance store. Required for stores whose query is mutated at runtime via reconnect(). */
  standalone?: boolean;
};

export type CollectionStoreConnectOptions = {
  onError?: (error: Error) => void;
};

export type CollectionStoreReconnectOptions = {
  queryOptions?: QueryOptions;
  onError?: (error: Error) => void;
};

const canonicalizeQueryOptions = (options?: QueryOptions): string => {
  if (!options) return '';
  // Cursor-bearing filters carry DocumentSnapshot instances which aren't
  // serializable; standalone stores carry those, not registry-backed ones.
  const orderBy = options.orderBy
    ? `o:${options.orderBy.field}:${options.orderBy.direction}`
    : '';
  const filters = (options.extraConstraints ?? [])
    .map((f) => {
      switch (f.type) {
        case 'where': return `w:${f.field}:${f.op}:${JSON.stringify(f.value)}`;
        case 'orderBy': return `o:${f.field}:${f.direction}`;
        case 'limit': return `l:${f.n}`;
        case 'startAt':
        case 'startAfter':
        case 'endAt':
        case 'endBefore':
          return `c:${f.type}`;
      }
    })
    .join('|');
  return `${orderBy}#${filters}`;
};

export type CollectionStore<T extends Record<string, unknown>> = {
  readonly type: 'collection';
  readonly path: string;
  readonly state: CollectionState<T>;
  readonly connected: boolean;
  connect: (options?: CollectionStoreConnectOptions) => Promise<void>;
  disconnect: () => void;
  reconnect: (options?: CollectionStoreReconnectOptions) => Promise<void>;
  update: (docId: string, data: UpdateData<T>, options?: PersistOperationOptions) => Promise<void>;
  set: (docId: string, data: T, options?: PersistOperationOptions) => Promise<void>;
  setMerge: (docId: string, data: Partial<T>, options?: PersistOperationOptions) => Promise<void>;
  add: (data: WithFieldValue<T>, options?: PersistOperationOptions) => Promise<string>;
  delete: (docId: string, options?: PersistOperationOptions) => Promise<void>;
};

export function createCollectionStore<T extends Record<string, unknown>>(
  path: string,
  options?: CollectionStoreOptions,
): CollectionStore<T> {
  if (path.includes('__inactive__')) {
    return createInertCollectionStore<T>(path);
  }

  const standalone = options?.standalone ?? false;
  const initialQueryOptions = options?.queryOptions;
  const registryKey = standalone
    ? null
    : `${path}#${canonicalizeQueryOptions(initialQueryOptions)}`;

  if (registryKey !== null) {
    const existing = registry.get(registryKey);
    if (existing) {
      return existing as CollectionStore<T>;
    }
  }

  const state = createCollectionState<T>();

  let persist: CollectionPersist<T> | null = null;
  let unsubscribe: (() => void) | null = null;
  let refCount = 0;
  let epoch = 0;
  let currentQueryOptions: QueryOptions | undefined = initialQueryOptions;

  const resetState = (): void => {
    state.docs.clear();
    state.docIds.length = 0;
    state.loading = true;
  };

  const store: CollectionStore<T> & { forceDisconnect: () => void } = {
    type: 'collection' as const,
    path,
    state,
    connected: false,

    async connect(connectOptions?) {
      refCount++;
      if (store.connected) return;
      if (registryKey !== null) registry.set(registryKey, store);
      (store as { connected: boolean }).connected = true;
      if (persist) return;
      epoch++;
      const myEpoch = epoch;
      const result = persistCollection<T>(
        path,
        state,
        connectOptions?.onError,
        currentQueryOptions,
        () => myEpoch === epoch,
      );
      persist = result.persist;
      unsubscribe = result.unsubscribe;
    },

    disconnect() {
      refCount = Math.max(0, refCount - 1);
      if (refCount > 0 || !store.connected) return;
      persist?.cancelPending();
      unsubscribe?.();
      persist = null;
      unsubscribe = null;
      resetState();
      (store as { connected: boolean }).connected = false;
      if (registryKey !== null) registry.delete(registryKey);
    },

    async reconnect(reconnectOptions?) {
      if (!store.connected) return;
      if (!standalone && reconnectOptions?.queryOptions !== undefined) {
        throw new Error(`[store] reconnect with new queryOptions requires standalone: true at "${path}".`);
      }
      persist?.cancelPending();
      unsubscribe?.();
      resetState();
      epoch++;
      const myEpoch = epoch;
      if (reconnectOptions?.queryOptions !== undefined) {
        currentQueryOptions = reconnectOptions.queryOptions;
      }
      const result = persistCollection<T>(
        path,
        state,
        reconnectOptions?.onError,
        currentQueryOptions,
        () => myEpoch === epoch,
      );
      persist = result.persist;
      unsubscribe = result.unsubscribe;
    },

    async update(docId, data, updateOptions?) {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      return persist.update(docId, data, updateOptions);
    },

    async set(docId, data, setOptions?) {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      return persist.set(docId, data, setOptions);
    },

    async setMerge(docId, data, mergeOptions?) {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      return persist.setMerge(docId, data, mergeOptions);
    },

    async add(data, addOptions?) {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      return persist.add(data, addOptions);
    },

    async delete(docId, deleteOptions?) {
      if (!persist) throw new Error(`[store] "${path}" is not connected.`);
      return persist.delete(docId, deleteOptions);
    },

    forceDisconnect() {
      refCount = 0;
      store.disconnect();
    },
  };

  if (registryKey !== null) registry.set(registryKey, store);
  return store;
}

function createInertCollectionStore<T extends Record<string, unknown>>(
  path: string,
): CollectionStore<T> {
  const state = createCollectionState<T>();
  state.loading = false;

  const inactivePath = path;
  const noop = async (): Promise<void> => {};
  const inertWrite = async (): Promise<never> => {
    throw new Error(`[store] inert __inactive__ store at "${inactivePath}" rejects writes.`);
  };
  const inertAdd = async (): Promise<string> => {
    throw new Error(`[store] inert __inactive__ store at "${inactivePath}" rejects writes.`);
  };

  return {
    type: 'collection' as const,
    path,
    state,
    connected: false,
    connect: noop,
    disconnect: () => {},
    reconnect: noop,
    update: inertWrite,
    set: inertWrite,
    setMerge: inertWrite,
    add: inertAdd,
    delete: inertWrite,
  };
}
